import React from 'react';
import { FiCheck, FiX } from 'react-icons/fi';
import { DEFAULT_PROCEED_OPTIONS, type QuickReplyOption } from '../../utils/proceedPrompt';
import '../../styles/ProceedQuickReplies.css';

const YES_RE = /^(yes|y|ok|okay|confirm|approve|proceed|continue)$/i;
const NO_RE = /^(no|n|cancel|reject|deny|stop)$/i;

/**
 * One-click replies to an AI question — Yes / No for a plain "Do you want me to
 * proceed?", or the labels an AI sent via $$SHOWMODAL_<text><Buttons>[A|B]$$.
 * The host decides when to render it and sends the reply through its normal
 * send path. `showButtons={false}` keeps just the question (an older, already
 * answered prompt).
 */
const ProceedQuickReplies: React.FC<{
  onReply: (text: string) => void;
  options?: QuickReplyOption[];
  text?: string;
  showButtons?: boolean;
  disabled?: boolean;
  className?: string;
}> = ({ onReply, options = DEFAULT_PROCEED_OPTIONS, text, showButtons = true, disabled = false, className }) => {
  if (!text && (!showButtons || options.length === 0)) return null;
  return (
    <div className={`proceed-replies${className ? ` ${className}` : ''}`}>
      {text && <div className="proceed-replies__text">{text}</div>}
      {showButtons && options.length > 0 && (
        <div className="proceed-replies__buttons" role="group" aria-label={text || 'Quick replies'}>
          {options.map((opt, i) => {
            const isYes = YES_RE.test(opt.label.trim());
            const isNo = NO_RE.test(opt.label.trim());
            // Yes-like labels (or the first option when none is) get the primary style.
            const primary = isYes || (i === 0 && !options.some((o) => YES_RE.test(o.label.trim())));
            return (
              <button
                key={`${opt.label}-${i}`}
                type="button"
                className={`proceed-replies__btn ${primary ? 'proceed-replies__btn--yes' : 'proceed-replies__btn--no'}`}
                onClick={() => onReply(opt.reply)}
                disabled={disabled}
                title={`Reply "${opt.reply}"`}
              >
                {isYes && <FiCheck size={12} />}
                {isNo && <FiX size={12} />}
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProceedQuickReplies;
